import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { saasApi } from '../../utils/saasApi';

export default function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await saasApi.login(email.trim(), password);
      navigate('/superadmin/tenants', { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-zinc-900 to-neutral-900 px-4"
      data-testid="super-admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-8 w-8 text-amber-300" />
          </div>
          <h1
            className="text-3xl font-bold text-white"
            style={{ fontFamily: 'Manrope' }}
          >
            ECare 360 — Platform Console
          </h1>
          <p className="text-white/70 mt-2 text-base">
            Super-admin sign-in. Manage tenants, plans &amp; billing.
          </p>
        </div>
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-4">
            <CardTitle
              className="text-xl text-center"
              style={{ fontFamily: 'Manrope' }}
            >
              Sign in as Platform Owner
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                  data-testid="sa-login-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="sa-email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="sa-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@saas.com"
                  className="h-12"
                  required
                  data-testid="sa-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sa-password" className="text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="sa-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Owner password"
                  className="h-12"
                  required
                  data-testid="sa-login-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 text-base bg-amber-600 hover:bg-amber-700"
                data-testid="sa-login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
